import React, { useContext, useRef, useState } from "react";
import {
  Avatar,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InstallMobileRounded from "@mui/icons-material/InstallMobileRounded";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import InstallAppDialog from "components/common/InstallAppDialog";
import SettingsDialog from "./SettingsDialog";
import { DrawerContext } from "context/DrawerContext";
import { useAuth } from "hooks/useAuth";
import { useInstallApp } from "hooks/useInstallApp";

/**
 * Who is signed in, pinned to the foot of the sidebar, and the account things that
 * don't earn a full nav row: Settings, install, log out. On the mini rail
 * only the avatar shows and the menu opens to the side.
 *
 * `topBar` is the Google-style version for a full-screen app's top-right corner
 * (CareMap): just the avatar, with the menu opening downward.
 */
const AccountMenu = ({ showSettings, onNavigate, onLogout, topBar = false }) => {
  const inExpandedDrawer = useContext(DrawerContext);
  const expanded = inExpandedDrawer && !topBar;
  const { user, userData } = useAuth();
  const { t } = useTranslation();
  const { supported, installed, isHandheld } = useInstallApp();
  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Sign-up never asks for a name, so the email (or phone) is who they are.
  const contact = user?.email || user?.phoneNumber || userData?.phone || "";
  const label = contact || "Your account";
  const initial = contact.charAt(0).toUpperCase();
  const canInstall = supported && !installed && isHandheld;

  const closeMenu = () => setMenuOpen(false);

  const avatar = (
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: "primary.main",
        color: "primary.contrastText",
      }}
    >
      <Typography variant="body2">
        {initial || <PersonRounded fontSize="small" />}
      </Typography>
    </Avatar>
  );

  const placement = topBar
    ? {
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        transformOrigin: { vertical: "top", horizontal: "right" },
      }
    : {
        anchorOrigin: expanded
          ? { vertical: "top", horizontal: "left" }
          : { vertical: "bottom", horizontal: "right" },
        transformOrigin: { vertical: "bottom", horizontal: "left" },
      };

  return (
    <>
      {topBar ? (
        <IconButton
          ref={anchorRef}
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          sx={{ p: 0.5 }}
        >
          {avatar}
        </IconButton>
      ) : (
        <ListItemButton
          ref={anchorRef}
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          sx={[
            { mx: 1, my: 1, px: 1, py: 0.75, borderRadius: 2, flexGrow: 0 },
            expanded ? {} : { justifyContent: "center" },
          ]}
        >
          {avatar}
          {expanded && (
            <>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ variant: "body2", noWrap: true }}
                sx={{ minWidth: 0, ml: 1.5, my: 0 }}
              />
              <ExpandMoreRounded fontSize="small" color="action" />
            </>
          )}
        </ListItemButton>
      )}

      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={closeMenu}
        {...placement}
        slotProps={{
          paper: {
            sx: {
              minWidth: 240,
              width: expanded ? anchorRef.current?.clientWidth : undefined,
            },
          },
        }}
      >
        {/* The rail shows only the avatar, so there the menu says who is signed in. */}
        {!expanded && [
          <Typography key="who" variant="body2" color="text.secondary" noWrap sx={{ px: 2, py: 1 }}>
            {label}
          </Typography>,
          // Same 8px above and below that MUI gives a divider after a MenuItem.
          <Divider key="who-divider" sx={{ my: 1 }} />,
        ]}
        {showSettings && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onNavigate?.();
              setSettingsOpen(true);
            }}
          >
            <ListItemIcon>
              <SettingsRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("accountMenu.settings")}</ListItemText>
          </MenuItem>
        )}
        {canInstall && (
          <MenuItem
            onClick={() => {
              closeMenu();
              setInstallOpen(true);
            }}
          >
            <ListItemIcon>
              <InstallMobileRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("accountMenu.install")}</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            closeMenu();
            onLogout?.();
          }}
        >
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("accountMenu.logOut")}</ListItemText>
        </MenuItem>
      </Menu>

      {showSettings && (
        <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      )}
      {canInstall && (
        <InstallAppDialog open={installOpen} onClose={() => setInstallOpen(false)} />
      )}
    </>
  );
};

export default AccountMenu;
